import { ChevronRightIcon, ChevronLeftIcon } from "@heroicons/react/solid";
import { createRef, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

const Carousel = ({
  items = [],
  renderItem,
  id,
  renderHeader = () => null,
  showMoreLink = null,
  paddingClass = "px-10",
  platformColors = false
}) => {
  // TODO: move platform colors up to the app-level
  const { platformData } = useSelector(state => state.frontend);
  const style = platformColors
    ? {
        backgroundColor: `${platformData.lightColor}`,
        color: `${platformData.darkColor}`
      }
    : {};

  const sliderDiv = useRef(null);
  const [scrolledItemIndex, setScrolledItemIndex] = useState(0); // For detecting start scroll position

  const ItemComp = renderItem;
  const HeaderComp = renderHeader;

  const refs = items.map(() => createRef());

  const [itemWidth, setItemWidth] = useState(0);

  // set itemWidth when ref is initialized
  useEffect(() => {
    const itemWidth = refs[0].current?.offsetWidth;
    if (itemWidth) setItemWidth(itemWidth);
  }, [refs]);

  const scrollHorizontally = shift => {
    const sliderWidth = sliderDiv.current.offsetWidth;
    const showMoreButtonAdjustment = showMoreLink ? 1 : 0;
    const itemsPerPage = Math.floor(sliderWidth / itemWidth) + showMoreButtonAdjustment;
    const nextItemIndex = itemsPerPage * shift + scrolledItemIndex;
    let scrollOffset;
    if (refs[nextItemIndex]?.current) {
      scrollOffset =
        sliderDiv.current.scrollLeft +
        refs[nextItemIndex].current.getBoundingClientRect().x -
        refs[nextItemIndex].current.getBoundingClientRect().width;
      setScrolledItemIndex(scrolledItemIndex + shift * itemsPerPage);
    } else if (!refs[nextItemIndex]?.current && shift === 1) {
      scrollOffset = sliderDiv.current.getBoundingClientRect().x - itemWidth;
      setScrolledItemIndex(0);
    } else if (!refs[nextItemIndex]?.current && shift === -1) {
      scrollOffset = sliderDiv.current.scrollWidth;
      setScrolledItemIndex(Math.ceil(items.length - itemsPerPage));
    }

    sliderDiv.current.scrollTo({ left: scrollOffset, behavior: "smooth" });
  };

  return (
    <div style={style} id={id}>
      <div
        className={`flex justify-between items-center mx-3 md:mx-auto px-0 sm:${paddingClass} pb-2 sm:pb-7`}
      >
        <HeaderComp />
        <div className=" gap-2 md:gap-5 flex items-center">
          {items.length > 1 && (
            <>
              <LeftScrollButton onClick={() => scrollHorizontally(-1)} />
              <RightScrollButton onClick={() => scrollHorizontally(1)} />
            </>
          )}
        </div>
      </div>
      <div
        ref={sliderDiv}
        className={`sm:${paddingClass} flex pl-2 overflow-x-auto md:scroll-bar hide-scroll-bar relative`}
      >
        <div className="flex flex-nowrap">
          <ul className="flex space-x-2 sm:space-x-5 flex-no-wrap">
            {items.map((item, idx) => (
              <ItemComp item={item} key={idx} innerRef={refs[idx]} />
            ))}

            {showMoreLink && (
              <>
                <Link to={showMoreLink} className="flex">
                  <button
                    style={{ width: itemWidth }}
                    className="hover:opacity-70 hover:bg-opacity-70 text-xl font-medium rounded-md"
                  >
                    See More
                  </button>
                </Link>
                {/* Quick workaround to fix the edge case when scrolling to the right */}
                <div style={{ width: itemWidth / 2 }}></div>
              </>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export const LeftScrollButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      type="button"
      className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm hover:text-gray-600 text-gray-500  opacity-50 bg-gray-300 hover:opacity-100 hover: focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
    >
      <ChevronLeftIcon className="h-5 w-5 md:h-8 md:w-8" />
    </button>
  );
};

export const RightScrollButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      type="button"
      className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm hover:text-gray-600 text-gray-500  opacity-50 bg-gray-300 hover:opacity-100 hover: focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
    >
      <ChevronRightIcon className="h-5 w-5 md:h-8 md:w-8" />
    </button>
  );
};

export default Carousel;
